<template>
  <MeteringPointCard :metering-point="meteringPoint" class="p-4 md:p-6">
    <div class="flex flex-col gap-4">
      <div class="flex flex-col justify-between gap-8 lg:flex-row">
        <div
          class="grid h-fit shrink-0 grid-cols-[auto_auto] gap-x-4 gap-y-3 *:odd:text-gray-300 *:even:font-medium"
        >
          <template v-for="(item, index) in sdeFields" :key="index">
            <p>
              {{ item.label }}
              <WikiTooltipComponent
                v-if="item.wikiPath"
                :path="item.wikiPath"
              />
            </p>
            <p>{{ item.value }}</p>
          </template>
        </div>
      </div>
      <BaseCard class="max-h-180 overflow-x-auto">
        <BaseTable :columns="sdeFinancialColumns" :rows="sdeFinancialRows">
          <template
            v-for="column in Object.keys(sdeFinancialColumns)"
            :key="column"
            #[`${column}-max-row`]="{ row }"
          >
            <p class="font-bold">{{ row[column] }}</p>
          </template>
        </BaseTable>
      </BaseCard>
    </div>
  </MeteringPointCard>
</template>

<script setup>
import { computed } from "vue"
import { formatCurrency, formatKW } from "@/helpers/formatter.js"
import { BaseTable, BaseCard } from "@repowerednl/ui-component-library"
import MeteringPointCard from "@/components/panels/MeteringPointCard.vue"
import WikiTooltipComponent from "@/components/information/WikiTooltipComponent.vue"

const props = defineProps({
  meteringPoint: {
    type: Object,
    default: null,
  },
})

const assetProps = computed(() => props.meteringPoint?.properties || {})

const sde = computed(() => {
  if (!assetProps.value.sde_decision) {
    return {}
  }

  const sdeDecision = assetProps.value.sde_decision
  return {
    decisionYear: sdeDecision.year,
    power: formatKW(sdeDecision.subsidized_power, "Wp"),
    totalHours: sdeDecision.full_load_hours,
  }
})

const sdeFields = computed(() => [
  { label: "Beschikkingsjaar", value: sde.value.decisionYear || "-" },
  { label: "Nominaal vermogen (kWp)", value: sde.value.power || "-" },
  { label: "Vollasturen", value: sde.value.totalHours || "-" },
  {
    label: "Verwacht subsidiebedrag (per MWh)",
    value: expectedSubsidy.value || "-",
    wikiPath: "/nl/platform/asset-information/strike-price",
  },
])

const sdeFinancialColumns = {
  financial_data: {
    label: "Financiële gegevens",
  },
  net_delivery: {
    label: "Netlevering",
  },
  non_net_delivery: {
    label: "Niet netlevering",
  },
}

const expectedSubsidy = computed(() => {
  const service = assetProps.value.flex_services?.IMBALANCE_CURTAILMENT
  if (service && service.strike_price !== null && service.is_active) {
    // The expected subsidy is calculated as the negative of the strike price
    return formatCurrency(-service.strike_price)
  }
  return null
})

const sdeFinancialRows = computed(() => {
  if (!assetProps.value.sde_decision) {
    return []
  }

  const sdeDecision = assetProps.value.sde_decision
  return [
    {
      financial_data: "Fasebedrag",
      net_delivery: `${formatCurrency(sdeDecision.phase_price)} per MWh`,
      non_net_delivery: `${formatCurrency(sdeDecision.phase_price)} per MWh`,
      _id: "phase-row",
    },
    {
      financial_data: "Basisbedrag",
      net_delivery: `${formatCurrency(sdeDecision.base_energy_price_feed_in)} per MWh`,
      non_net_delivery: `${formatCurrency(sdeDecision.base_energy_price_not_feed_in)} per MWh`,
      _id: "base-row",
    },
    {
      financial_data: "Maximale subsidietarief",
      net_delivery: `${formatCurrency(sdeDecision.phase_price - sdeDecision.base_energy_price_feed_in)} per MWh`,
      non_net_delivery: `${formatCurrency(sdeDecision.phase_price - sdeDecision.base_energy_price_not_feed_in)} per MWh`,
      _id: "max-row",
    },
  ]
})
</script>
